/**
 * ThresholdInput — free-form decimal entry for exclusion rule thresholds.
 * Keeps a local draft while focused so typing "50" does not fight a leading 0.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useEffect, useState } from 'react';

import {
  DECIMAL_PATTERN,
  formatThreshold,
  parseThreshold,
} from './threshold-input.parse';

export interface ThresholdInputProps {
  value: number;
  onChange: (next: number) => void;
  ariaLabel: string;
}

const ThresholdInput: FC<ThresholdInputProps> = ({ value, onChange, ariaLabel }) => {
  const [draft, setDraft] = useState(() => formatThreshold(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(formatThreshold(value));
  }, [value, focused]);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft}
      onFocus={(e) => {
        setFocused(true);
        e.currentTarget.select();
      }}
      onChange={(e) => {
        const next = e.target.value;
        if (next === '' || DECIMAL_PATTERN.test(next)) setDraft(next);
      }}
      onBlur={() => {
        setFocused(false);
        const parsed = parseThreshold(draft);
        onChange(parsed);
        setDraft(formatThreshold(parsed));
      }}
      className="h-8 w-20 rounded-md border border-input bg-background px-2 text-right text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-ring"
      aria-label={ariaLabel}
    />
  );
};

export default ThresholdInput;
